// AttachmentViewer.tsx

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, IconButton, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

interface Attachments {
  id: string
  iconLink: string
  mimeType: string
  name: string
  thumbnailLink: string
  url: string
}

function normalizePdfUrl(url: string) {
  try {
    const u = new URL(url, window.location.href)
    const host = u.hostname.toLowerCase()

    // Google Drive: convert to /preview
    // Accept both /file/d/<id>/view and open?id=<id> styles
    if (host.includes('drive.google.com')) {
      // /file/d/<id>...
      const m = u.pathname.match(/\/file\/d\/([^/]+)/)
      const id = m?.[1] || u.searchParams.get('id')
      if (id) return `https://drive.google.com/file/d/${id}/preview`
    }

    // Dropbox: force "raw" (inline) instead of dl=1 (download)
    if (host.endsWith('dropbox.com')) {
      u.searchParams.set('raw', '1')
      u.searchParams.delete('dl')
      return u.toString()
    }

    return url
  } catch {
    return url
  }
}

const PDFInline: React.FC<{ url: string; title: string; height?: number }> = ({ url, title, height = 600 }) => {
  // Attempt 1: direct inline (normalized URL)
  const direct = useMemo(() => normalizePdfUrl(url), [url])

  // If inline viewer fails (blank due to headers/provider), fallback to Google Docs Viewer
  const gview = useMemo(() => `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`, [url])

  const [useFallback, setUseFallback] = useState(false)

  // Heuristic fallback: swap to gview after a short delay if user still sees blank.
  // (We can't reliably "onError" an iframe for PDF, and cross-origin blocks document probing.)
  useEffect(() => {
    setUseFallback(false)
    const id = window.setTimeout(() => setUseFallback(true), 1200)
    return () => window.clearTimeout(id)
  }, [direct])

  return useFallback ? (
    <iframe
      src={gview}
      title={`${title} (preview)`}
      width="100%"
      height={height}
      allow="fullscreen"
      allowFullScreen
      style={{ border: 'none', background: '#fff' }}
    />
  ) : (
    // Direct inline attempt (best UX if headers allow)
    <iframe
      src={`${direct}#toolbar=1`} // tweak toolbar params if desired
      title={title}
      width="100%"
      height={height}
      allow="fullscreen"
      allowFullScreen
      style={{ border: 'none', background: '#fff' }}
    />
  )
}

interface AttachmentViewerProps {
  open: boolean
  attachments: Attachments[]
  currentIndex: number
  onClose: () => void
  onNavigate: (direction: 'prev' | 'next') => void
}

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  open,
  attachments,
  currentIndex,
  onClose,
  onNavigate,
}) => {
  const theme = useTheme()
  const attachment = attachments[currentIndex]
  const isImage = attachment?.mimeType?.startsWith('image/')
  const isPdf = attachment?.mimeType === 'application/pdf' || attachment?.name?.toLowerCase()?.endsWith('.pdf')

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!open) return
    if (e.key === 'ArrowLeft') onNavigate('prev')
    if (e.key === 'ArrowRight') onNavigate('next')
    if (e.key === 'Escape') onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '90vw',
          maxWidth: '65vw',
          height: 'auto',
          maxHeight: '95vh',
          background: 'transparent',
          bgcolor: theme.palette.background.defaultdim,
          position: 'relative',
        },
      }}
      onKeyDown={e => handleKeyDown(e as any)}
    >
      <DialogContent
        sx={{
          background: 'transparent',
          bgcolor: theme.palette.background.defaultdim,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <IconButton
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backdropFilter: 'blur(10px)',
            bgcolor: 'background.defaultdim',
          }}
          onClick={onClose}
        >
          <CloseIcon sx={{ color: 'text.primary' }} />
        </IconButton>

        {currentIndex > 0 && (
          <IconButton
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              backdropFilter: 'blur(10px)',
              bgcolor: 'background.defaultdim',
            }}
            onClick={() => onNavigate('prev')}
          >
            <ArrowBackIcon sx={{ color: 'text.primary' }} />
          </IconButton>
        )}
        {currentIndex < attachments.length - 1 && (
          <IconButton
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              backdropFilter: 'blur(10px)',
              bgcolor: 'background.defaultdim',
            }}
            onClick={() => onNavigate('next')}
          >
            <ArrowForwardIcon sx={{ color: 'text.primary' }} />
          </IconButton>
        )}

        <Box
          sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 6 }}
        >
          {isImage ? (
            <img
              src={attachment.url}
              alt={attachment.name}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : isPdf ? (
            <PDFInline url={attachment.url} title={attachment.name} height={600} />
          ) : (
            <Box sx={{ color: '#fff' }}>Preview not supported</Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default AttachmentViewer
